import React from 'react';
import { css } from '@emotion/react';
import { gql } from '@yi/core';

import { breakpoints } from '~/src/styles';

import { BaseCarousel, BaseCarouselComposerProps } from './BaseCarousel';
import { CarouselPlaceholder } from './CarouselPlaceholder';
import { WithPlaceholder } from './types';
import { ClassCard } from '../cards';

type CarouselItem = gql.typesClient.Fragment_ClassCard;
type Props = BaseCarouselComposerProps<CarouselItem>;

type PlaceholderType = Pick<Props, 'button' | 'heading' | 'headerCSS'>;
const Placeholder: React.FC<PlaceholderType> = ({ button, headerCSS, heading }) => (
  <CarouselPlaceholder
    cardDimension={ClassCard.dimension}
    headerDimensions={{ headerHeight: 17, headerWidth: 186 }}
    noButton={button === null}
    headerCSS={headerCSS}
    heading={heading}
    data-test-percy-hidden
  />
);

/**
 * Safari 15 doesn't support overflow: clip
 * Without setting overflow clip we are not able to set hidden by x axis and set visible by y axis
 * if you set to one of the axis some value other than 'visible', the other axis will become 'auto'.
 * to be able to set consistent paddings in all places, we need this ugly hack to overcome the limitations for Safari 15.
 * we add an offset that should be enough for the overflowing content (note: shadows are also require an offset)
 * and then "shrink" the size with negative margin-bottom
 */
const overflowedContendHeight = 100;
const overrideSlickClassCarouselCSS = css({
  '.slick-list': {
    [breakpoints.minDesktop]: {
      marginBottom: `-${overflowedContendHeight}px`,
    },
  },
  '.slick-slide': {
    [breakpoints.minDesktop]: {
      marginBottom: `${overflowedContendHeight}px`,
    },
  },
});

export const ClassCarousel: React.FC<Props> & WithPlaceholder<PlaceholderType> = (props: Props) => {
  if (!props.items?.length) return null;
  return (
    <BaseCarousel<CarouselItem>
      renderCard={cardProps => <ClassCard {...cardProps} />}
      getReactKey={c => c.title}
      {...props}
      overrideSlickCarouselCSS={[props.overrideSlickCarouselCSS, overrideSlickClassCarouselCSS]}
    />
  );
};

ClassCarousel.Placeholder = Placeholder;
